import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, FileText, ShieldCheck, X } from "lucide-react";
import { api, type PublicPolicy, type PublicPolicySummary } from "../lib/api";
import { Card, CARD_TARGET_CLASS } from "../components/beui/Card";
import { Modal } from "../components/beui/Modal";
import { PolicyMetadata, PolicyReader } from "../components/legal/PolicyReader";
import { useSafeSearch } from "../lib/router-safe";
import "./LegalHub.css";

function PolicyReadingModal({ selected, onClose }: { selected: Pick<PublicPolicySummary, "kind" | "title">; onClose: () => void }) {
  const [document, setDocument] = useState<PublicPolicy | null>(null);
  return <Modal label={document?.title ?? selected.title} onClose={onClose}>
    <header className="legal-reader-head">
      <div><h2 className="text-lg font-bold text-navy">{document?.title ?? selected.title}</h2>
        {document && <PolicyMetadata document={document} />}</div>
      <button type="button" className="legal-reader-close" aria-label="Close document" autoFocus onClick={onClose}><X className="w-5 h-5" aria-hidden="true" /></button>
    </header>
    <div className="legal-reader-body" tabIndex={0} role="region" aria-label="Policy content"><PolicyReader kind={selected.kind} onDocument={setDocument} /></div>
    <footer className="legal-reader-foot"><small className="text-mut">Reading does not record acceptance.</small><button type="button" className="btn-outline" onClick={onClose}>Close</button></footer>
  </Modal>;
}

export default function LegalHub() {
  const [, navigate] = useLocation();
  const search = useSafeSearch();
  const requestedKind = new URLSearchParams(search).get("document");
  const [documents, setDocuments] = useState<PublicPolicySummary[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  // Navigation links and cards share URL-owned selection so reload and browser
  // history reopen the same current reader without invoking legal acceptance.
  const selected = requestedKind ? {
    kind: requestedKind,
    title: documents.find((document) => document.kind === requestedKind)?.title ?? "Policy",
  } : null;
  useEffect(() => {
    let active = true;
    setState("loading");
    api.publishedPolicies().then((catalog) => {
      if (active) {
        const rank = (kind: string) => kind === "terms" ? 0 : kind === "privacy" ? 1 : kind === "settlement-terms" ? 2 : 3;
        setDocuments([...catalog.documents].sort((left, right) => rank(left.kind) - rank(right.kind)));
        setState("ready");
      }
    }).catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [attempt]);

  return <div className="legal-hub container-x pb-12">
    <header className="py-12 sm:py-16 text-center"><h1 className="text-3xl sm:text-4xl font-extrabold text-navy">Legal hub</h1></header>
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <h2 className="text-base font-bold text-navy">Platform policies</h2>
      <p className="text-xs text-mut">Available without signing in</p>
    </div>
    {state === "loading" && <p role="status" className="text-mut py-6">Loading policies…</p>}
    {state === "error" && <div role="alert" className="card p-6">
      <p>Could not load the policies. Please try again.</p>
      <button type="button" className="btn-outline mt-3" onClick={() => setAttempt((value) => value + 1)}>Retry policies</button>
    </div>}
    {state === "ready" && documents.length === 0 && <p className="text-mut py-6">No policies have been published yet.</p>}
    {state === "ready" && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {documents.map((document) => <Card key={document.kind}>
        <span className="mb-6 inline-flex w-10 h-10 rounded-xl items-center justify-center text-brand-red bg-red-50" aria-hidden="true"><FileText className="w-5 h-5" /></span>
        <h3 className="text-lg font-bold mb-2">
          <button type="button" className={`${CARD_TARGET_CLASS} text-left`} onClick={() => navigate(`/legal?document=${encodeURIComponent(document.kind)}`)} aria-haspopup="dialog">{document.title}</button>
        </h3>
        <p className="text-sm text-mut leading-relaxed">{document.description}</p>
        <span aria-hidden="true" className="flex items-center gap-2 mt-auto pt-6 text-xs font-bold text-brand-red">Read document <ArrowRight className="w-4 h-4" /></span>
      </Card>)}
    </div>}
    <aside className="flex flex-wrap sm:flex-nowrap items-center gap-4 mt-8 rounded-2xl bg-navy-50 border border-line p-6" aria-label="Related guidance">
      <ShieldCheck className="w-7 h-7 text-navy shrink-0" aria-hidden="true" />
      <div><h2 className="text-sm font-bold text-navy">Trust &amp; Safety</h2><p className="text-xs text-mut mt-1">Practical guidance for a safer marketplace experience.</p></div>
      <Link href="/safety" className="sm:ml-auto text-sm font-semibold text-brand-red py-3">Read our safety guidance →</Link>
    </aside>
    {selected && <PolicyReadingModal key={selected.kind} selected={selected} onClose={() => navigate("/legal", { replace: true })} />}
  </div>;
}
